import { Link, useSearchParams } from "react-router";

import type { TenantSlug } from "@/lib/theme-presets";
import { cn } from "@/lib/utils";

import { INSTRUCTOR_STATES, STATES_GROUPS, type InstructorState, type Outcome, type StatesGroup } from "./fixtures";
import { InstructorView } from "./InstructorView";
import { StatesView } from "./StatesView";

/**
 * The box primitive's specimen: States (every kind in every state) and the
 * Instructor scenario (every result pressed). `/design-system?boxes=<view>&state=<state>`
 * draws one view alone, in the tenant the hostname chose, for captures, the
 * judge and the press harness; `&outcome=<result>` sets the Instructor's next
 * commit result, as its picker does.
 */

type View = "states" | "instructor";
const OUTCOMES: Outcome[] = ["confirmed", "refused", "unknown", "partial"];

const VIEWS: [View, string, readonly (readonly [string, string])[]][] = [
  ["states", "States", STATES_GROUPS],
  ["instructor", "Instructor", INSTRUCTOR_STATES],
];

const href = (view: View, state: string) => `/design-system?boxes=${view}&state=${state}`;

function pick<T extends string>(options: readonly (readonly [T, string])[], value: string | null): T {
  return options.find(([id]) => id === value)?.[0] ?? options[0][0];
}

/** Every view and state, as links: each is its own page. */
function SpecimenIndex({ current }: { current?: { view: View; state: string } }) {
  return (
    <nav aria-label="Box examples" className="grid gap-2">
      {VIEWS.map(([view, label, states]) => (
        <p key={view} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-ui">
          <span className="font-semibold text-foreground">{label}</span>
          {states.map(([state, name]) => {
            const here = current?.view === view && current.state === state;
            return (
              <Link key={state} to={href(view, state)} aria-current={here ? "page" : undefined} className={cn("text-primary hover:underline", here && "font-semibold text-foreground")}>
                {name}
              </Link>
            );
          })}
        </p>
      ))}
    </nav>
  );
}

/** One view alone, chosen by the query. */
export function BoxesFocus({ tenant }: { tenant: TenantSlug }) {
  const [params] = useSearchParams();
  const view: View = params.get("boxes") === "instructor" ? "instructor" : "states";
  if (view === "instructor") {
    const state = pick<InstructorState>(INSTRUCTOR_STATES, params.get("state"));
    const next = OUTCOMES.find((outcome) => outcome === params.get("outcome")) ?? "confirmed";
    return (
      <div className="grid gap-6">
        <SpecimenIndex current={{ view, state }} />
        <InstructorView key={`${tenant}-${state}-${next}`} tenant={tenant} state={state} next={next} />
      </div>
    );
  }
  const group = pick<StatesGroup>(STATES_GROUPS, params.get("state"));
  return (
    <div className="grid gap-6">
      <h1 className="text-title">Boxes in every state</h1>
      <SpecimenIndex current={{ view, state: group }} />
      <StatesView group={group} />
    </div>
  );
}

/** The design system's Boxes section: links to each view, and every state drawn. */
export function BoxesSection({ idPrefix }: { idPrefix: string }) {
  return (
    <section aria-labelledby={`${idPrefix}-boxes`} className="space-y-4">
      <div>
        <h2 id={`${idPrefix}-boxes`} className="text-tile-title">Boxes</h2>
        <p className="text-ui text-muted-foreground">A box answers a question, finds records, or changes something. Each example below is its own page; open one alone to try it.</p>
      </div>
      <SpecimenIndex />
      <StatesView group="all" />
    </section>
  );
}
